<template>
  <OperationFormLayoutSkeleton v-if="loading" />
  <OperationFormLayout
    v-if="!loading && currentOrganisation"
    :id="operationId"
    :formSaving="saving"
    operationType="plant"
    @cancel="$router.back()"
    @update="save"
  >
    <OperationFormSection
      :modifiers="['border-radius-top']"
      :sectionDescription="$t('operations.details_description')"
      :sectionTitle="$t('operations.details_title')"
    >
      <FormRow
        :errors="nameErrors"
        :label="$t('operations.operation_name_label_plant')"
        labelFor="operation_name"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <div :class="{ input: true, 'input--error': nameErrors }">
            <input
              id="operation_name"
              v-model="operation.name"
            >
          </div>
        </div>
      </FormRow>
      <FormRow
        :label="$t('operations.operation_division_label')"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <DivisionSelect
            v-model="operation.division"
            :divisions="divisions"
          />
        </div>
      </FormRow>
      <FormRow
        v-if="operation.division && operation.division.isNew"
        :errors="divisionNameErrors"
        :label="$t('operations.operation_new_division_label')"
        labelFor="new_division_name"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <div :class="{ input: true, 'input--error': divisionNameErrors }">
            <input
              id="new_division_name"
              v-model="operation.division.name"
            >
          </div>
        </div>
      </FormRow>
      <FormRow
        :label="$t('equipment.type')"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <Select
            v-model="operation.plant_type_id"
            :items="types"
            :modifiers="['input', 'base', 'short-dropdown']"
            :placeholder="displayType"
          />
        </div>
      </FormRow>
      <FormRow
        :label="$t('operations.operation_time_zone')"
        :modifiers="['no-padding']"
      >
        <div class="form-row__input form-row__input--no-peek">
          <Select
            v-model="operation.time_zone"
            :items="timeZones"
            :modifiers="['input', 'base', 'short-dropdown']"
            :placeholder="displayTimeZone"
          />
        </div>
      </FormRow>
    </OperationFormSection>
    <OperationFormSection
      :modifiers="['border-radius-bottom']"
      :sectionDescription="$t('operations.bounds_description')"
      :sectionTitle="$t('operations.bounds_title')"
    >
      <FormRow
        :errors="boundsErrors"
        :modifiers="['no-padding']"
      >
        <button
          :aria-label="$t('operations.edit_bounds')"
          :class="{
            'operation-settings__bounds-button': true,
            'operation-settings__bounds-button--error': boundsErrors,
          }"
          type="button"
          @click="boundsEditorOpen = true"
        >
          <MapPreview
            canvasClass="operation-settings__bounds-preview"
            :emptyText="$t('operations.no_bounds')"
            emptyClass="operation-settings__no-bounds"
            :polygon="operation.bounds"
          />
        </button>
      </FormRow>
    </OperationFormSection>
  </OperationFormLayout>

  <teleport
    v-if="boundsEditorOpen"
    to="body"
  >
    <BoundsEditor
      v-model="operation.bounds"
      :overlays="operation.map_tile_overlays"
      @close="boundsEditorOpen = false"
    />
  </teleport>
</template>

<script setup>
import { computed, onMounted, reactive, ref, inject } from 'vue';
import { useRouter } from 'vue-router';
import I18n from 'i18n-js';

// Utils
import dig from '@/functions/dig';
import timeZones from '@/functions/time_zones';
import useAjax from '@/functions/use_ajax';

// Components
import BoundsEditor from '@/components/operations/bounds_editor.vue';
import DivisionSelect from '@/components/operations/division_select.vue';
import FormRow from '@/components/form/row.vue';
import MapPreview from '@/components/generic/map_preview.vue';
import OperationFormLayout from '@/components/operations/form/operation_form_layout.vue';
import OperationFormLayoutSkeleton from '@/components/operations/form/operation_form_layout_skeleton.vue';
import OperationFormSection from '@/components/operations/form/operation_form_section.vue';
import Select from '@/components/form/select.vue';

const props = defineProps({
  organisationId: { type: String, required: true },
  operationId: { type: String, default: null },
});

const router = useRouter();
const ajax = useAjax();
const loading = ref(false);
const currentOrganisation = inject('currentOrganisation');
const initialOperation = ref(null);
const operation = reactive(createNewOperation());
const boundsEditorOpen = ref(false);
const errors = ref(null);
const saving = ref(false);
const divisions = ref(null);
const types = ref([]);

const nameErrors = computed(() => dig(errors.value, 'operation', 'name'));
const divisionNameErrors = computed(() =>
  dig(errors.value, 'division', 'division', 'name')
);
const boundsErrors = computed(() =>
  dig(errors.value, 'operation', 'bounds')
);
const displayType = computed(() => {
  return operation.type ? operation.type : I18n.t('equipment.no_type');
});
const displayTimeZone = computed(() => {
  return operation.time_zone
    ? operation.time_zone
    : I18n.t('operations.no_operation_time_zone');
});

function createNewOperation() {
  return {
    name: '',
    bounds: null,
    division: null,
    plant_type_id: '',
    map_tile_overlays: [],
    time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

// All endpoints that this component uses
const ENDPOINTS = {
  divisions: `/organisations/${props.organisationId}/divisions`,
  operations: `/organisations/${props.organisationId}/operations`,
  operation: `/organisations/${props.organisationId}/operations/${props.operationId}`,
  types: `/organisations/${props.organisationId}/plant_types`,
  get post_or_patch() {
    return props.operationId ? this.operation : this.operations;
  }
};

onMounted(() => {
  loadDivisions();
  loadOperation();
  loadTypes();
});

async function loadDivisions() {
  let { ok, data } = await ajax(ENDPOINTS.divisions);

  if (ok) {
    divisions.value = data.divisions;
  }
}

async function loadOperation() {
  if (props.operationId) {
    loading.value = true;
    let { ok, data } = await ajax(ENDPOINTS.operation);

    if (ok) {
      initialOperation.value = data;
      Object.assign(operation, data);
      loading.value = false;
    }
  } else {
    initialOperation.value = createNewOperation();
  }
}

const loadTypes = async () => {
  let { ok, data } = await ajax(ENDPOINTS.types);

  if (ok) {
    types.value = data?.map((item) => ({ value: item.id, label: item.name }));
    types.value.unshift({ value: '', label: I18n.t('equipment.no_type'), dim: true });
  }
};

function formatDivisionAttributes() {
  if (operation.division?.isNew) {
    return {
      division_id: 'new',
      new_division: operation.division.name,
    };
  } else {
    return {
      division_id: operation.division?.id || null,
    };
  }
}

function formatOperationForPost() {
  return {
    operation: {
      name: operation.name,
      bounds: operation.bounds,
      operation_type: 'plant',
      plant_type_id: operation.plant_type_id,
      time_zone: operation.time_zone,
      ...formatDivisionAttributes(),
    },
  };
}

async function save() {
  errors.value = null;
  saving.value = true;

  let method = props.operationId ? 'PATCH' : 'POST';
  let { ok, data } = await ajax(ENDPOINTS.post_or_patch, {
    method,
    body: formatOperationForPost(),
  });

  if (ok) {
    router.replace({ name: 'operation', params: { operationId: data.id } });
  } else {
    errors.value = data;
  }

  saving.value = false;
}
</script>
